import { useEffect, useState } from "react";
import { Search } from "lucide-react";

import { Button } from "@/components/Button";
import { Filters } from "@/components/Filters";
import { Input } from "@/components/Input";
import { PresetDropdown } from "@/components/PresetDropdown";
import { ResultModal } from "@/components/ResultModal";
import { ResultsTable } from "@/components/ResultsTable";
import { EmptyState, ErrorState, Spinner } from "@/components/states";
import { Star } from "lucide-react";

import { useAddFavorite } from "@/hooks/useFavorites";
import { useEnrich } from "@/hooks/useEnrich";
import { useSearch } from "@/hooks/useSearch";
import { useHuntStore } from "@/stores/hunt";
import type { Filters as F, SearchRequest } from "@/types";

export function Hunt() {
  const search = useSearch();
  const enrich = useEnrich();
  const addFav = useAddFavorite();

  // Inputs are local for typing responsiveness; the persisted store holds the
  // *last searched* params + results so the view survives leaving Hunt (and F5).
  // Seed the inputs from the persisted last search on mount — this relies on the
  // store's sessionStorage backend hydrating synchronously before first render
  // (true for session/localStorage; would need a rehydrate effect if ever async).
  const storeForm = useHuntStore((s) => s.form);
  const result = useHuntStore((s) => s.result);
  const [query, setQuery] = useState(storeForm.query);
  const [preset, setPreset] = useState(storeForm.preset);
  const [filters, setFilters] = useState<F>(storeForm.filters);

  // Run a search. useSearch commits the params + result to the persisted store on
  // success only (NEW-1) — so a failed search never clobbers the saved view.
  function runSearch(req: SearchRequest) {
    search.mutate(req);
  }

  function saveSearch() {
    // Save exactly the search that produced the visible results (the store's last
    // search), not any unsearched edits still sitting in the inputs.
    addFav.mutate(
      storeForm.preset
        ? { kind: "search", preset: storeForm.preset, filters: storeForm.filters }
        : { kind: "search", query: (storeForm.query ?? "").trim(), filters: storeForm.filters },
    );
  }

  // Replay a search picked from History / saved-search favorites.
  const replay = useHuntStore((s) => s.replay);
  const setReplay = useHuntStore((s) => s.setReplay);
  useEffect(() => {
    if (!replay) return;
    // Don't stack a replay on top of an in-flight search (NEW-2) — two searches
    // would race and the last to resolve would win, desyncing rows from the form.
    // isPending is in the deps, so the replay is consumed once the current search
    // settles rather than being dropped.
    if (search.isPending) return;
    setQuery(replay.query ?? "");
    setPreset(replay.preset ?? "");
    setFilters(replay.filters ?? {});
    runSearch(replay);
    setReplay(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replay, search.isPending]);

  const busy =
    enrich.state && enrich.state.status !== "done" && enrich.state.status !== "failed"
      ? { fullName: enrich.state.fullName, kind: enrich.state.kind }
      : null;

  function run() {
    if (preset) {
      runSearch({ preset, filters });
      return;
    }
    const q = query.trim();
    if (!q) return;
    runSearch({ query: q, filters });
  }

  return (
    <main className="mx-auto w-full max-w-6xl px-6 py-6">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run();
        }}
        className="space-y-3"
      >
        <div className="flex gap-2">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={preset ? "пресет выбран — запрос игнорируется" : "qimen, 八字, tarot…"}
            aria-label="Поисковый запрос"
            disabled={!!preset}
            className="flex-1 disabled:opacity-50"
          />
          <PresetDropdown value={preset} onChange={setPreset} />
          <Button type="submit" disabled={search.isPending}>
            <Search size={16} /> Искать
          </Button>
          {result !== null && (
            <Button
              type="button"
              variant="ghost"
              onClick={saveSearch}
              disabled={addFav.isPending}
              title="Сохранить этот поиск"
            >
              <Star size={16} />
            </Button>
          )}
        </div>
        <Filters value={filters} onChange={setFilters} />
      </form>

      {search.isPending && <Spinner label="Ищем на GitHub…" />}
      {!search.isPending && search.isError && <ErrorState>Не удалось выполнить поиск.</ErrorState>}
      {!search.isPending && !search.isError && !result && (
        <EmptyState>Выберите пресет или введите запрос и нажмите «Искать».</EmptyState>
      )}
      {!search.isPending &&
        !search.isError &&
        result &&
        (result.repos.length === 0 ? (
          <EmptyState>Ничего не найдено.</EmptyState>
        ) : (
          <>
            {result.rate_limited && (
              <div className="mt-4 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                Достигнут лимит GitHub — показаны частичные результаты. Повторите поиск через минуту.
              </div>
            )}
            <ResultsTable
              repos={result.repos}
              remaining={result.rate_limit_remaining}
              total={result.total_count}
              onAction={(repo, kind) => enrich.start(repo, kind)}
              onFavorite={(repo) => addFav.mutate({ kind: "repo", full_name: repo.full_name })}
              favPending={addFav.isPending}
              busy={busy}
            />
          </>
        ))}

      {enrich.state && (
        <ResultModal
          state={enrich.state}
          onClose={enrich.close}
          favPending={addFav.isPending}
          onFavoriteIdea={(idx) => {
            const id = enrich.state?.enrichment?.id;
            if (id != null) addFav.mutate({ kind: "idea", enrichment_id: id, idea_index: idx });
          }}
        />
      )}
    </main>
  );
}
